import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Check,
  Clock,
  Loader2,
  ShieldAlert,
  ShieldCheck,
  X,
} from "lucide-react";
import { AdminPage } from "@/components/AdminPage";
import { useToast } from "@/components/Toast";
import { api } from "../lib/api";
import { mapAdminApiError } from "../lib/errors";
import type { ApiContract } from "@podecontratar/shared/contract/index";
import type { EndpointOutput } from "@podecontratar/shared/contract/endpoint";

type ReportRow = EndpointOutput<ApiContract["GET /admin/reports"]>[number];

const CATEGORY_LABEL: Record<string, string> = {
  purpose_mismatch: "Finalidade não corresponde",
  unauthorized: "Não autorizei a consulta",
  unauthorized_party: "Terceiro não autorizado",
  sensitive_data_leak: "Dados sensíveis expostos",
  fraud_suspicion: "Suspeita de fraude",
  other: "Outra",
};

const STATUS_OPTIONS = [
  { value: "all", label: "Todas" },
  { value: "pending", label: "Em análise" },
  { value: "reviewed", label: "Revisadas" },
  { value: "dismissed", label: "Arquivadas" },
];

const WINDOWS = [
  { value: 7, label: "7 dias" },
  { value: 30, label: "30 dias" },
  { value: 90, label: "90 dias" },
  { value: 365, label: "1 ano" },
];

const STATUS_STYLE: Record<string, string> = {
  pending: "bg-warning/10 text-warning border-warning/30",
  reviewed: "bg-accent-soft text-accent border-accent/30",
  dismissed: "bg-surface-alt text-text-tertiary border-border",
};

const STATUS_LABEL: Record<string, string> = {
  pending: "Em análise",
  reviewed: "Revisada",
  dismissed: "Arquivada",
};

function formatDateTime(iso: string | null): string {
  if (!iso) return "—";
  return new Date(iso.replace(" ", "T") + "Z").toLocaleString("pt-BR", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

/**
 * The report's underlying verification can be gone by the time this list is
 * read (a hard-deleted account, say); `target_type`/`target_document` are
 * then null and this renders a dash instead of formatting garbage.
 */
function formatDoc(type: "pf" | "pj" | null, doc: string | null): string {
  if (!type || !doc) return "—";
  if (type === "pf" && doc.length === 11) {
    return `${doc.slice(0, 3)}.${doc.slice(3, 6)}.${doc.slice(6, 9)}-${doc.slice(9)}`;
  }
  if (type === "pj" && doc.length === 14) {
    return `${doc.slice(0, 2)}.${doc.slice(2, 5)}.${doc.slice(5, 8)}/${doc.slice(8, 12)}-${doc.slice(12)}`;
  }
  return doc;
}

type ReportsData = {
  reports: ReportRow[];
  total: number;
  counts: Array<{ status: string; n: number }>;
  windowDays: number;
  nextCursor: string | null;
};

export default function AdminAbuseReportsPage() {
  const toast = useToast();
  const [status, setStatus] = useState<string>("pending");
  const [days, setDays] = useState(30);
  const [data, setData] = useState<ReportsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selected, setSelected] = useState<ReportRow | null>(null);
  const [saving, setSaving] = useState(false);
  const [reviewerNotes, setReviewerNotes] = useState("");

  const fetchReports = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.request("GET /admin/reports", {
        query: { days, status: status !== "all" ? status : undefined, limit: 100 },
      });
      const meta = res.meta as { total: number; window_days: number; counts: Array<{ status: string; n: number }> };
      setData({
        reports: res.items,
        total: meta.total,
        counts: meta.counts,
        windowDays: meta.window_days,
        nextCursor: res.next_cursor,
      });
    } catch (err) {
      setError(mapAdminApiError(err));
    } finally {
      setLoading(false);
    }
  }, [days, status]);

  useEffect(() => {
    void fetchReports();
  }, [fetchReports]);

  async function loadMore() {
    if (!data?.nextCursor || loadingMore) return;
    setLoadingMore(true);
    try {
      const res = await api.request("GET /admin/reports", {
        query: { days, status: status !== "all" ? status : undefined, limit: 100, cursor: data.nextCursor },
      });
      setData((prev) =>
        prev
          ? {
              ...prev,
              reports: [...prev.reports, ...res.items],
              nextCursor: res.next_cursor,
            }
          : prev
      );
    } catch {
      toast.push("Não foi possível carregar mais denúncias.", "error");
    } finally {
      setLoadingMore(false);
    }
  }

  useEffect(() => {
    if (selected) setReviewerNotes(selected.reviewer_notes ?? "");
  }, [selected]);

  const totals = useMemo(() => {
    const out: Record<string, number> = { pending: 0, reviewed: 0, dismissed: 0 };
    for (const row of data?.counts ?? []) out[row.status] = row.n;
    return out;
  }, [data]);

  async function setReportStatus(
    next: "pending" | "reviewed" | "dismissed",
    notes: string | null
  ) {
    if (!selected) return;
    setSaving(true);
    try {
      await api.request("POST /admin/reports/:id/status", {
        params: { id: selected.id },
        body: { status: next, notes },
      });
      toast.push("Status atualizado.", "success");
      await fetchReports();
      setSelected(null);
    } catch (err) {
      toast.push(mapAdminApiError(err, "Não foi possível atualizar o status."), "error");
    } finally {
      setSaving(false);
    }
  }

  const filters = (
    <div className="flex flex-wrap items-center gap-2">
      <div className="inline-flex items-center gap-1 rounded-lg border border-border bg-surface p-1">
        {STATUS_OPTIONS.map((opt) => {
          const count =
            opt.value === "all"
              ? (data?.total ?? 0)
              : (totals[opt.value] ?? 0);
          const active = status === opt.value;
          return (
            <button
              key={opt.value}
              type="button"
              onClick={() => setStatus(opt.value)}
              className={`inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-medium tracking-tight transition-colors ${
                active
                  ? "bg-surface-alt text-text"
                  : "text-text-tertiary hover:text-text"
              }`}
            >
              {opt.label}
              <span
                className={`rounded-full px-1.5 py-0.5 text-[10px] tabular-nums ${
                  active ? "bg-text/10 text-text" : "bg-surface-alt text-text-tertiary"
                }`}
              >
                {count}
              </span>
            </button>
          );
        })}
      </div>
      <div className="inline-flex items-center gap-1 rounded-lg border border-border bg-surface p-1">
        {WINDOWS.map((w) => (
          <button
            key={w.value}
            type="button"
            onClick={() => setDays(w.value)}
            className={`rounded-md px-3 py-1.5 text-xs font-medium tracking-tight transition-colors ${
              days === w.value
                ? "bg-surface-alt text-text"
                : "text-text-tertiary hover:text-text"
            }`}
          >
            {w.label}
          </button>
        ))}
      </div>
    </div>
  );

  return (
    <AdminPage
      eyebrow="Operacional"
      title="Denúncias"
      description="Reports enviados por titulares contra consultas. Triage manual."
      action={filters}
    >
      {error && (
        <div className="mb-6 rounded-2xl border border-danger/30 bg-danger/10 p-4 text-sm text-text">
          {error}
        </div>
      )}

      {loading && !data ? (
        <div className="grid place-items-center rounded-3xl border border-border bg-surface py-20">
          <Loader2 size={20} className="animate-spin text-text-tertiary" aria-hidden />
        </div>
      ) : data && data.reports.length === 0 ? (
        <div className="rounded-3xl border border-border bg-surface py-12 text-center">
          <ShieldCheck
            size={28}
            strokeWidth={1.5}
            className="mx-auto text-text-tertiary"
            aria-hidden
          />
          <p className="mt-4 text-sm font-medium text-text">
            Sem denúncias no período.
          </p>
          <p className="mt-1 text-xs text-text-tertiary">
            Quando um titular denunciar uma consulta, ela aparece aqui.
          </p>
        </div>
      ) : data ? (
        <div className="overflow-hidden rounded-3xl border border-border bg-surface">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border bg-surface-alt text-[11px] font-medium uppercase tracking-[0.16em] text-text-tertiary">
                <th className="hidden px-3 py-3 text-left sm:table-cell sm:px-5">Data</th>
                <th className="hidden px-3 py-3 text-left md:table-cell md:px-5">Reporter</th>
                <th className="px-3 py-3 text-left sm:px-5">Alvo da consulta</th>
                <th className="hidden px-3 py-3 text-left lg:table-cell lg:px-5">Consultante</th>
                <th className="hidden px-3 py-3 text-left md:table-cell md:px-5">Categoria</th>
                <th className="px-3 py-3 text-left sm:px-5">Status</th>
              </tr>
            </thead>
            <tbody>
              {data.reports.map((r) => (
                <tr
                  key={r.id}
                  onClick={() => setSelected(r)}
                  className="cursor-pointer border-b border-border last:border-0 transition-colors hover:bg-surface-alt"
                >
                  <td className="hidden whitespace-nowrap px-3 py-4 text-text-secondary sm:table-cell sm:px-5">
                    <p>{formatDateTime(r.created_at)}</p>
                  </td>
                  <td className="hidden px-3 py-4 text-text md:table-cell md:px-5">
                    <p className="truncate font-medium">{r.reporter_name ?? "—"}</p>
                    <p className="truncate text-[11px] text-text-tertiary">
                      {r.reporter_email}
                    </p>
                  </td>
                  <td className="px-3 py-4 text-text sm:px-5">
                    <p className="truncate font-medium">
                      {r.target_name_provided ?? formatDoc(r.target_type, r.target_document)}
                    </p>
                    <p className="truncate text-[11px] text-text-tertiary">
                      {r.target_type ? `${r.target_type.toUpperCase()} · ` : ""}
                      {formatDoc(r.target_type, r.target_document)}
                    </p>
                  </td>
                  <td className="hidden px-3 py-4 text-text lg:table-cell lg:px-5">
                    <p className="truncate font-medium">{r.requester_name ?? "—"}</p>
                    <p className="truncate text-[11px] text-text-tertiary">
                      {r.requester_email}
                    </p>
                  </td>
                  <td className="hidden px-3 py-4 text-text-secondary md:table-cell md:px-5">
                    {CATEGORY_LABEL[r.category] ?? r.category}
                  </td>
                  <td className="px-3 py-4 sm:px-5">
                    <span
                      className={`inline-flex items-center gap-1 whitespace-nowrap rounded-full border px-2.5 py-0.5 text-[10px] font-semibold uppercase tracking-[0.16em] ${
                        STATUS_STYLE[r.status]
                      }`}
                    >
                      {r.status === "pending" ? (
                        <Clock size={10} strokeWidth={2.5} aria-hidden />
                      ) : r.status === "reviewed" ? (
                        <ShieldCheck size={10} strokeWidth={2.5} aria-hidden />
                      ) : (
                        <ShieldAlert size={10} strokeWidth={2.5} aria-hidden />
                      )}
                      {STATUS_LABEL[r.status]}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}

      {data?.nextCursor && (
        <div className="mt-5 flex justify-center">
          <button
            type="button"
            onClick={loadMore}
            disabled={loadingMore}
            className="inline-flex items-center gap-1.5 rounded-full border border-border bg-surface px-4 py-2 text-xs font-medium text-text transition-colors hover:bg-surface-alt disabled:cursor-not-allowed disabled:opacity-60"
          >
            {loadingMore && <Loader2 size={13} className="animate-spin" aria-hidden />}
            Carregar mais
          </button>
        </div>
      )}

      {/* ─── DETAIL DRAWER ─────────────────────────────────────────── */}
      {selected && (
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby="report-detail-title"
          className="fixed inset-0 z-[100] flex items-start justify-center overflow-y-auto bg-text/60 p-4 backdrop-blur-sm sm:items-center sm:p-6"
          onClick={(e) => {
            if (e.target === e.currentTarget && !saving) setSelected(null);
          }}
        >
          <div className="relative w-full max-w-xl overflow-hidden rounded-3xl border border-border bg-surface shadow-[var(--shadow-card-lift)]">
            <button
              type="button"
              onClick={() => setSelected(null)}
              disabled={saving}
              className="absolute right-4 top-4 z-10 rounded-md p-1.5 text-text-tertiary transition-colors hover:bg-surface-alt hover:text-text disabled:opacity-50"
              aria-label="Fechar"
            >
              <X size={16} aria-hidden />
            </button>
            <div className="p-7 sm:p-8">
              <p className="text-[11px] font-medium uppercase tracking-[0.22em] text-text-tertiary">
                {CATEGORY_LABEL[selected.category] ?? selected.category}
              </p>
              <h3
                id="report-detail-title"
                className="mt-2 text-lg font-semibold tracking-tight text-text"
              >
                Denúncia #{selected.id.slice(0, 8)}
              </h3>

              <dl className="mt-6 grid grid-cols-[auto_1fr] gap-x-5 gap-y-2.5 text-sm">
                <dt className="text-text-tertiary">Data</dt>
                <dd className="text-text">{formatDateTime(selected.created_at)}</dd>
                <dt className="text-text-tertiary">Reporter</dt>
                <dd className="text-text">
                  {selected.reporter_name ?? "—"}
                  <span className="ml-1 text-text-tertiary">
                    · {selected.reporter_email}
                  </span>
                </dd>
                <dt className="text-text-tertiary">Alvo</dt>
                <dd className="text-text">
                  {selected.target_name_provided ??
                    formatDoc(selected.target_type, selected.target_document)}{" "}
                  <span className="text-text-tertiary">
                    · {selected.target_type ? `${selected.target_type.toUpperCase()} · ` : ""}
                    {formatDoc(selected.target_type, selected.target_document)}
                  </span>
                </dd>
                <dt className="text-text-tertiary">Consultante</dt>
                <dd className="text-text">
                  {selected.requester_name ?? "—"}
                  <span className="ml-1 text-text-tertiary">
                    · {selected.requester_email}
                  </span>
                </dd>
                <dt className="text-text-tertiary">Status</dt>
                <dd>
                  <span
                    className={`inline-flex items-center gap-1 whitespace-nowrap rounded-full border px-2.5 py-0.5 text-[10px] font-semibold uppercase tracking-[0.16em] ${
                      STATUS_STYLE[selected.status]
                    }`}
                  >
                    {STATUS_LABEL[selected.status]}
                  </span>
                </dd>
                {selected.reviewed_at && (
                  <>
                    <dt className="text-text-tertiary">Revisada em</dt>
                    <dd className="text-text">
                      {formatDateTime(selected.reviewed_at)}
                    </dd>
                  </>
                )}
              </dl>

              {selected.message && (
                <div className="mt-6 rounded-xl border border-border bg-surface-alt p-4">
                  <p className="text-[11px] font-medium uppercase tracking-[0.22em] text-text-tertiary">
                    Mensagem
                  </p>
                  <p className="mt-2 whitespace-pre-wrap text-sm leading-relaxed text-text">
                    {selected.message}
                  </p>
                </div>
              )}

              <div className="mt-6">
                <label
                  htmlFor="reviewer-notes"
                  className="block text-sm font-medium tracking-tight text-text"
                >
                  Notas internas{" "}
                  <span className="text-text-tertiary">· opcional</span>
                </label>
                <textarea
                  id="reviewer-notes"
                  value={reviewerNotes}
                  onChange={(e) => setReviewerNotes(e.target.value)}
                  rows={3}
                  maxLength={2000}
                  placeholder="Contexto da revisão, decisão tomada, próximos passos…"
                  className="mt-2 w-full rounded-lg border border-border bg-surface px-4 py-2.5 text-sm text-text outline-none placeholder:text-text-tertiary focus:border-accent focus:ring-2 focus:ring-accent/20"
                />
              </div>

              <div className="mt-7 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
                {selected.status !== "pending" && (
                  <button
                    type="button"
                    onClick={() =>
                      setReportStatus("pending", reviewerNotes || null)
                    }
                    disabled={saving}
                    className="inline-flex items-center justify-center gap-1.5 rounded-lg border border-border bg-surface px-4 py-2.5 text-sm font-medium text-text-secondary transition-colors hover:bg-surface-alt disabled:opacity-50"
                  >
                    <Clock size={13} strokeWidth={1.75} aria-hidden />
                    Reabrir
                  </button>
                )}
                <button
                  type="button"
                  onClick={() =>
                    setReportStatus("dismissed", reviewerNotes || null)
                  }
                  disabled={saving || selected.status === "dismissed"}
                  className="inline-flex items-center justify-center gap-1.5 rounded-lg border border-border bg-surface px-4 py-2.5 text-sm font-medium text-text transition-colors hover:bg-surface-alt disabled:opacity-50"
                >
                  <X size={13} strokeWidth={1.75} aria-hidden />
                  Arquivar
                </button>
                <button
                  type="button"
                  onClick={() =>
                    setReportStatus("reviewed", reviewerNotes || null)
                  }
                  disabled={saving || selected.status === "reviewed"}
                  className="inline-flex items-center justify-center gap-2 rounded-lg bg-accent px-5 py-2.5 text-sm font-medium text-text-inverse transition-[background-color,transform] duration-150 hover:bg-accent-hover active:translate-y-px disabled:opacity-50"
                >
                  {saving ? (
                    <>
                      <Loader2 size={14} className="animate-spin" aria-hidden />
                      Salvando…
                    </>
                  ) : (
                    <>
                      <Check size={13} strokeWidth={2} aria-hidden />
                      Marcar revisada
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </AdminPage>
  );
}
